import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { formatDate } from '../lib/constants'

// Admin → Conversations. Who is talking to whom, how much, and whether it
// led to a booking — without message text. Text can only be opened (and
// every opening is logged) when a conversation has a report, a
// contact-details flag or a dispute (deleted messages included, marked).
// Enforced in the database by
// admin_conversations() / admin_conversation_messages()
// (sql/2026-11-04-admin-conversations.sql, 2026-11-04-messages-soft-delete.sql).
// Conversations where both people are test accounts are left out.

const cell = { padding: '8px 12px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' }
const headCell = { ...cell, padding: '10px 12px', textAlign: 'left', color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', whiteSpace: 'nowrap' }
const dateTime = (iso) => iso ? new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '—'
const needsReview = (c) => c.has_report || c.has_flag || c.has_dispute

const FILTERS = [
{ id: 'all', label: 'All' },
{ id: 'review', label: '⚠️ Report / flag / dispute' },
{ id: 'booked', label: '✅ Led to a booking' },
{ id: 'none', label: '💬 No booking yet' },
]

function outcomeLabel(c) {
if (c.outcome === 'booking') return { text: `Booking · ${c.status}${c.paid ? ' · paid' : ''}`, cls: 'badge-green' }
if (c.outcome === 'led_to_booking') return { text: 'Led to a booking', cls: 'badge-green' }
return { text: 'No booking yet', cls: 'badge-gray' }
}

export default function AdminConversations({ filterKey, cutoff }) {
const [rows, setRows] = useState(null)
const [error, setError] = useState('')
const [filter, setFilter] = useState('all')
const [hideHireIt, setHideHireIt] = useState(true) // hide chats with the HireIt account (hello@)
const [viewing, setViewing] = useState(null) // { convo, messages, error, loading }

useEffect(() => {
let cancelled = false
setRows(null); setError('')
supabase.rpc('admin_conversations', { p_since: cutoff }).then(({ data, error: err }) => {
if (cancelled) return
if (err) { setError(err.message); setRows([]); return }
setRows(data || [])
})
return () => { cancelled = true }
// eslint-disable-next-line
}, [filterKey])

const openConversation = async (c) => {
setViewing({ convo: c, messages: [], loading: true })
const { data, error: err } = await supabase.rpc('admin_conversation_messages', { p_booking_id: c.id })
setViewing({ convo: c, messages: data || [], loading: false, error: err ? err.message.replace(/^not_allowed: /, '') : '' })
}

if (rows === null) return <div className="card card-body" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Loading conversations…</div>

const hireItCount = rows.filter(c => c.involves_hireit).length
const base = hideHireIt ? rows.filter(c => !c.involves_hireit) : rows
const shown = base.filter(c => filter === 'all' ? true
: filter === 'review' ? needsReview(c)
: filter === 'booked' ? c.outcome !== 'none'
: c.outcome === 'none')
const booked = base.filter(c => c.outcome !== 'none').length
const review = base.filter(needsReview).length

return (
<div>
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
<h3 style={{ fontWeight: 700, color: 'var(--navy)', margin: 0 }}>{base.length} {hideHireIt ? 'user-to-user ' : ''}conversations</h3>
<label style={{ fontSize: 13, color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
<input type="checkbox" checked={hideHireIt} onChange={e => setHideHireIt(e.target.checked)} />
Hide HireIt admin messages{hireItCount ? ` (${hireItCount})` : ''}
</label>
</div>
<p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
With activity in this period. {booked} led to a booking · {review} with a report, contact-details flag or dispute. Conversations between two test accounts are left out.
Message text stays private — it can only be opened when there's a report, flag or dispute, and each view is logged.
</p>
{error && <div className="alert alert-error">Couldn't load conversations: {error}</div>}

<div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
{FILTERS.map(f => (
<button key={f.id} className={`btn btn-sm ${filter === f.id ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFilter(f.id)} style={{ fontSize: 12 }}>{f.label}</button>
))}
</div>

{shown.length === 0 ? (
<div className="card card-body" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No conversations here for this period.</div>
) : (
<div className="card" style={{ overflowX: 'auto' }}>
<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
<thead><tr>{['Started', 'Hirer → Owner', 'About', 'Messages', 'Last activity', 'Outcome', ''].map(h => <th key={h} style={headCell}>{h}</th>)}</tr></thead>
<tbody>
{shown.map(c => {
const o = outcomeLabel(c)
return (
<tr key={c.id}>
<td style={{ ...cell, whiteSpace: 'nowrap' }}>{formatDate(c.started_at)}</td>
<td style={cell}><strong style={{ fontWeight: 600 }}>{c.hirer_name || '—'}</strong> → {c.owner_name || '—'}{c.involves_hireit && <span className="badge badge-primary" style={{ marginLeft: 6 }}>HireIt</span>}</td>
<td style={cell}>{c.listing_title || (c.listing_id ? '(listing removed)' : <span style={{ color: 'var(--text-muted)' }}>Direct message</span>)}</td>
<td style={{ ...cell, textAlign: 'center' }}>{c.message_count}</td>
<td style={{ ...cell, whiteSpace: 'nowrap' }}>{dateTime(c.last_activity)}</td>
<td style={cell}>
<span className={`badge ${o.cls}`}>{o.text}</span>
{c.has_report && <span className="badge badge-red" style={{ marginLeft: 4 }}>Report</span>}
{c.has_flag && <span className="badge badge-red" style={{ marginLeft: 4 }}>🚩 Flag</span>}
{c.has_dispute && <span className="badge badge-red" style={{ marginLeft: 4 }}>Dispute</span>}
</td>
<td style={{ ...cell, whiteSpace: 'nowrap' }}>
{needsReview(c) && <button className="btn btn-ghost btn-sm" onClick={() => openConversation(c)} style={{ fontSize: 11 }}>View conversation</button>}
</td>
</tr>
)
})}
</tbody>
</table>
</div>
)}

{viewing && (
<div className="modal-overlay" onClick={() => setViewing(null)}>
<div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
<h3 style={{ fontWeight: 700, color: 'var(--navy)', margin: '0 0 4px' }}>{viewing.convo.hirer_name || '—'} → {viewing.convo.owner_name || '—'}</h3>
<p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 12px' }}>
{viewing.convo.listing_title || 'Direct message'} · Opened because of: {[viewing.convo.has_report && 'report', viewing.convo.has_flag && 'contact-details flag', viewing.convo.has_dispute && 'dispute'].filter(Boolean).join(', ')}. This view has been logged.
</p>
{viewing.loading ? (
<div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
) : viewing.error ? (
<div className="alert alert-error">{viewing.error}</div>
) : viewing.messages.length === 0 ? (
<div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No messages in this conversation.</div>
) : (
<div style={{ maxHeight: '55vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
{viewing.messages.map(m => {
const fromHirer = m.sender_id === viewing.convo.hirer_id
return (
<div key={m.id} style={{ alignSelf: fromHirer ? 'flex-start' : 'flex-end', maxWidth: '85%', background: fromHirer ? 'var(--primary-pale)' : 'var(--green-light)', borderRadius: 12, padding: '8px 12px', ...(m.deleted_at ? { opacity: 0.7, border: '1px dashed var(--red)' } : {}) }}>
<div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>{m.sender_name || '—'} · {dateTime(m.created_at)}</div>
{m.deleted_at && <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--red)', marginBottom: 2 }}>🗑️ Deleted by the sender {dateTime(m.deleted_at)} — hidden from both people</div>}
<div style={{ fontSize: 14, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{m.body}</div>
</div>
)
})}
</div>
)}
<div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
<button className="btn btn-ghost btn-sm" onClick={() => setViewing(null)}>Close</button>
</div>
</div>
</div>
)}
</div>
)
}
